/**
 * English texts for every label the library renders itself. Typed as the full
 * `Texts`, so a key added to the library fails the build until it is translated
 * here. Pass to <TextsProvider texts={englishTexts}> (or via textsFor("en")).
 */
export const englishTexts = {
    close: "Close",
    loading: "Loading",
    retry: "Retry",
    unknownError: "Unknown error",
    requestId: "Request ID",
    notifications: "Notifications",
    confidence: "Confidence",
    loadErrorTitle: "A new version is available",
    loadErrorText: "Part of the app could not load, usually because a new version was just deployed or your sign-in expired. Reloading fixes it; your work is saved.",
    renderErrorTitle: "This view could not be shown",
    renderErrorText: "Something went wrong while displaying it. Reload or go to the start page; nothing saved is lost.",
    reload: "Reload",
    home: "Go to start page",
    technicalDetail: "Technical detail",
    ageNow: "now",
    ageMin: "min",
    ageHour: "h",
    ageDay: "d",
    cancel: "Cancel",
    rename: "Rename",
    delete: "Delete",
    renameFailed: "Rename failed",
    deleteFailed: "Delete failed",
    untitled: "Untitled",
    confirmTitle: "Please confirm",
    confirmOk: "Confirm",
    assistantName: "AI assistant",
    assistantMark: "AI",
    assistantClose: "Close the AI assistant (Esc)",
    assistantTyping: "AI assistant is typing",
    assistantThinking: "Thinking…",
    assistantReasoning: "Reasoning",
    assistantToolRunning: "Looking up:",
    assistantToolError: "Not possible:",
    assistantToolDone: "Looked up:",
    assistantCopy: "Copy",
    assistantCopied: "Copied",
    assistantCopyAnswer: "Copy answer",
    assistantSend: "Send",
    assistantSendTitle: "Send (Enter)",
    assistantStop: "Stop the answer",
    assistantMessageLabel: "Message to the AI assistant",
    assistantPlaceholder: "Ask the AI assistant …",
    assistantAborted: "Stopped",
    assistantStreamError: "AI assistant error",
    assistantEmptyTitle: "How can I help?",
    assistantEmptyText: "You run suggestions yourself with a click.",
    assistantContinue: "Continue",
    assistantShowAll: "Show all conversations",
    assistantProposals: "Suggestions to run",
    assistantProposalRun: "Run the AI assistant's suggestion",
    assistantProposalFailed: "Run failed – try again",
    assistantProposalDone: "done",
    assistantConversations: "Conversations",
    assistantEarlier: "Earlier conversations",
    assistantHistory: "History",
    assistantAllOnPage: "All on their own page",
    assistantNewConversation: "New conversation",
    assistantNew: "New",
    assistantSearch: "Search conversations …",
    assistantNoConversations: "No conversations yet",
    assistantNoMatch: "No matching conversation",
    assistantNewTitle: "New title",
    assistantMessagesShort: "msgs",
    assistantContextSession: "With context",
    assistantGeneralSession: "General",
    assistantWider: "Wider",
    assistantNarrower: "Narrower",
    assistantOpenPage: "Open as page",
    assistantResize: "Resize the AI assistant (arrow keys, double-click: wide/narrow)",
    assistantResizeTitle: "Drag to resize · double-click: wide/narrow",
    assistantKeySend: "send",
    assistantKeyNewline: "new line",
    assistantKeyClose: "close",
    assistantToday: "Today",
    assistantYesterday: "Yesterday",
    assistantLast7: "Last 7 days",
    assistantOlder: "Older",
    assistantAttach: "Add image",
    assistantCamera: "Take photo",
    cameraTitle: "Take photo",
    cameraPreview: "Camera preview",
    cameraStarting: "Starting the camera …",
    cameraLive: "Camera ready. Press the shutter to take the photo.",
    cameraShutter: "Take the photo",
    cameraTaken: "Photo taken",
    cameraRetake: "Retake",
    cameraUse: "Use photo",
    cameraSwitch: "Switch camera",
    cameraFallback: "Choose a file instead",
    cameraDenied: "Camera access was refused. Allow it in the browser's site settings, or choose a file.",
    cameraNone: "No camera found. Choose a file instead.",
    cameraBusy: "The camera is in use by another app. Close it, or choose a file.",
    cameraFailed: "The camera could not start. Choose a file instead.",
    assistantRemoveAttachment: "Remove image",
    assistantDropImages: "Drop images here",
    assistantAttachmentType: "This image format is not supported",
    assistantAttachmentSize: "The image is too large",
    assistantAttachmentCount: "At most {n} images per message",
    assistantAttachmentFailed: "The image could not be uploaded",
    assistantPreparing: "Preparing image …",
    assistantUploading: "Uploading …",
    assistantImage: "Image",
    assistantImageTitle: "Photo question",
    assistantOpenImage: "Enlarge image",
    assistantSteps: "Steps ({n})",
    assistantStillWorking: "Still working: {step}",
    assistantStillWorkingPlain: "Still working …",
    assistantLookingAtImage: "Looking at the image …",
    assistantUploadProgress: "Uploaded: {percent} %",
    back: "Back",
    next: "Next",
    done: "Done",
    skip: "Skip",
    no: "No",
    search: "Search",
    paletteLabel: "Command palette",
    palettePlaceholder: "Search or type a command …",
    paletteResults: "Results",
    paletteSelect: "select",
    paletteRun: "run",
    shortcutsTitle: "Keyboard shortcuts",
    keyCtrl: "Ctrl",
    userMenu: "User menu",
    theme: "Appearance",
    themeLight: "Light",
    themeDark: "Dark",
    themeSystem: "System",
    themeLightTitle: "Always light",
    themeDarkTitle: "Always dark",
    themeSystemTitle: "Follow the operating system, switches with it",
    logout: "Sign out",
    tourClose: "End tour",
    tourCloseTitle: "End tour (Esc)",
    tourStepOf: "Step {n} of {total}",
    tourTryIt: "Try it:",
    tourLocked: "Locked during the tour",
    helpTitle: "Help & learning",
    helpSubtitle: "Learning path, guided tours and the handbook for every feature",
    helpPath: "Learning path",
    helpTours: "Tours",
    helpHandbook: "Handbook",
    helpSearch: "Search the handbook",
    helpSearchPlaceholder: "Search the handbook …",
    helpIntro: "Watch the introduction",
    helpReset: "Reset learning progress",
    helpResetConfirm: "Really reset your progress?",
    helpResetYes: "Yes, reset",
    helpNoResults: "Nothing found. Try another word.",
    pathTitle: "Your learning path",
    pathOf: "of {total}",
    pathMoodStart: "Each task takes only a moment. Most you simply do while working.",
    pathMoodHalf: "More than half done. The rest comes almost by itself while working.",
    pathMoodGoing: "Good start. Keep going, one task at a time.",
    pathMoodDone: "All learned. You now know every feature.",
    taskDone: "done",
    taskOpen: "open",
    taskDoneAt: "Done on {date}",
    toursIntro: "A tour highlights one area after another and explains it right in the app. Buttons are locked during the tour, so you cannot trigger anything by accident. Use [→] and [←] to page, [Esc] ends it.",
    tourDoneBadge: "done",
    tourMeta: "{minutes} min · {steps} steps",
    tourStart: "Start",
    tourAgain: "Again",
    articleTip: "Tip:",
    articleTourText: "Rather be shown? The tour walks you through this area.",
    articleTourStart: "Start tour",
    articleRelated: "Related",
    learnedTitle: "Learned: {title}",
    learnedProgress: "Learning path {done} of {total}",
    learnedAllTitle: "Learning path complete",
    learnedAllText: "You now know every feature.",
    welcomeLabel: "Introduction",
    welcomePages: "Pages of the introduction",
    welcomePage: "Page {n}",
    welcomeBrowse: "browse",
    document: "Document",
    docPageShort: "p.",
    docPages: "Pages",
    docPageLabel: "Page {n}",
    docPageError: "Page {n} could not be loaded",
    zoomIn: "Zoom in",
    zoomOut: "Zoom out",
    zoomFit: "Fit to width",
    docWider: "Show the document wide",
    docNarrower: "Make the document narrower again",
    docOpenOriginal: "Open original",
    docClose: "Close document",
    docNoEvidence: "no evidence in the document",
    docApprox: "handwritten · approximate position, p. {page} – please compare with the document",
    docApproxShort: "handwritten",
    docApproxTitle: "{label} · handwritten, approximate position",
    docMarkLabel: "Evidence for {label}",
    docOcr: "OCR",
    docOcrTitle: "Scanned page – text recognised by OCR",
    docWorkingTitle: "Reading the document …",
    docWorkingText: "Page images appear as soon as the extraction is done.",
    docNoPages: "No page images",
    docNoPagesText: "The original can be shown with “Open original”.",
    signingIn: "Signing in",
    checkingAccess: "Checking your access",
    noAccessTitle: "No access to this application yet",
    noAccessText: "Sign-in worked, but your account has no role for this application yet.",
    signedInAs: "Signed in as",
    email: "Email",
    signedInVia: "via",
    howToAccess: "How to get access",
    howToAccessText: "IT assigns the roles. Ask them for one of these roles:",
    copyRequest: "Copy request",
    copied: "Copied",
    signInAgain: "Got the role? Sign in again",
    noAccessNote: "Roles are picked up at sign-in. After the assignment, sign in again once; to use another account, go through “Sign out”.",
    // Lumi
    lumiMoodIdle: "{assistant} is here to help",
    lumiMoodListening: "{assistant} is listening",
    lumiMoodWorking: "{assistant} is working",
    lumiMoodSpeaking: "{assistant} is answering",
    lumiMoodGuiding: "{assistant} is guiding you",
    lumiMoodGuidingStep: "{assistant} is guiding you: step {n}, {title}",
    lumiMoodCareful: "Careful: confirm the safety step first",
    lumiMoodSolved: "Solved",
    lumiSourceOpen: "Open {name}",
    lumiSources: "Sources",
    lumiMoreSources: "More sources",
    lumiWebSources: "From the web",
    lumiWhy: "Why",
    lumiWorked: "It worked",
    lumiNotWorked: "Did not work",
    lumiSavedForTeam: "Saved for your team",
    lumiOriginKB: "From your documents",
    lumiOriginWeb: "From the web",
    lumiOriginMixed: "From documents and the web",
    lumiOriginGeneral: "General knowledge, not from your documents",
    lumiSolutionsFound: "{n} possible solutions",
    lumiGuideTitle: "Guided fix",
    lumiGuideStepOf: "Step {n} of about {m}",
    lumiGuideOpen: "Open the guided fix",
    lumiGuideSolved: "Solved",
    lumiGuideHandover: "Handed over",
    lumiGuideNotSolved: "Not solved",
    lumiGuideDanger: "Danger",
    lumiGuideCaution: "Caution",
    lumiGuideSafetyConfirm: "I have read this and the machine is safe",
    lumiGuideSafetyConfirmed: "Safety confirmed",
    lumiGuideExpected: "You should see",
    lumiGuideFigure: "Figure",
    lumiGuideAnswers: "Your answer",
    lumiGuideHistory: "Steps",
    lumiGuideStateDone: "done",
    lumiGuideStateCurrent: "current step",
    lumiGuideStateUpcoming: "coming up",
    lumiGuideAdapts: "The next steps adapt to your answers.",
    lumiGuideViewSwitch: "Guided fix or chat",
    lumiGuideTab: "Guide",
    lumiChatTab: "Chat",
    lumiHandoverTitle: "Handover summary",
    lumiHandoverIntro: "The fix is not finished. Pass this on to a colleague or the service.",
    lumiHandoverSymptom: "Symptom",
    lumiHandoverSteps: "Steps tried and results",
    lumiHandoverPhotos: "{n} photos in the conversation",
    lumiHandoverSources: "Manual pages",
    lumiHandoverNotAnswered: "not answered",
    lumiHandoverSummary: "Summary",
    lumiHandoverCopy: "Copy",
    lumiHandoverShare: "Share",
    lumiHandoverCopied: "Summary copied.",
    lumiHandoverCopyFailed: "The summary could not be copied.",
    lumiMachine: "Machine",
    lumiNoMachine: "No machine given",
    lumiFilterChoose: "Choose machine",
    lumiFilterChange: "Change machine",
    lumiFilterFor: "For {label}",
    lumiFilterRemove: "Remove machine filter",
    lumiFieldSolution: "Field solution",
};
